import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, Check, RotateCcw } from "lucide-react";

const questions = [
  { question: "What matters most when exploring IGET?", options: ["Flavour variety", "Simple product information", "Portability", "Service and delivery guidance"] },
  { question: "What would you like to learn first?", options: ["Available flavours", "Product formats", "How to use my device", "Support and returns"] },
  { question: "How should we organise the guide for you?", options: ["Quick overview", "Detailed comparison within IGET", "Troubleshooting first", "Australian service information"] },
];

export default function ProductRecommendationAdvanced() {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [complete, setComplete] = useState(false);

  const answer = (value: string) => {
    const next = [...answers, value];
    setAnswers(next);
    if (next.length === questions.length) setComplete(true);
    else setQuestionIndex((index) => index + 1);
  };

  const restart = () => {
    setAnswers([]);
    setQuestionIndex(0);
    setComplete(false);
  };

  if (complete) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 md:p-12 shadow-lg">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-6"><Check className="w-7 h-7 text-green-600" /></div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 mb-3">IGET guide ready</p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Your IGET information path is ready</h1>
            <p className="text-lg text-gray-600 mb-8">We organised your preferences into an IGET-focused guide. Review the product details, flavour information, and support resources before visiting the official product destination.</p>
            <div className="space-y-3 mb-8">{answers.map((item, index) => <div key={`${item}-${index}`} className="rounded-lg bg-gray-50 p-4"><span className="text-sm text-gray-500">Preference {index + 1}</span><p className="font-semibold text-gray-900">{item}</p></div>)}</div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/products/iget"><Button className="bg-blue-600 hover:bg-blue-700">Open IGET guide <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              <Button variant="outline" onClick={restart}><RotateCcw className="mr-2 w-4 h-4" />Retake quiz</Button>
            </div>
          </Card>
        </div>
      </main>
    );
  }

  const current = questions[questionIndex];
  const progress = ((questionIndex + 1) / questions.length) * 100;
  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="p-8 md:p-12 shadow-lg">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 mb-3">IGET discovery quiz</p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Find the right IGET guide</h1>
          <p className="text-gray-600 mb-8">Answer three questions and we will organise the most relevant IGET information for you.</p>
          <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Question {questionIndex + 1} of {questions.length}</span><span>{Math.round(progress)}%</span></div>
          <Progress value={progress} className="h-2 mb-8" />
          <h2 className="text-xl font-bold text-gray-900 mb-6">{current.question}</h2>
          <div className="space-y-3">{current.options.map((option) => <button key={option} onClick={() => answer(option)} className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"><span className="font-medium text-gray-800">{option}</span></button>)}</div>
        </Card>
      </div>
    </main>
  );
}
